import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, KeyRound, ArrowRight, Building2 } from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";
import { redirectAfterLogin } from "@/lib/redirect";

export default function Login() {
  const { login, submit2fa, selectAgency, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 2FA challenge state
  const [challenge, setChallenge] = useState(null); // {challenge_token, ttl_seconds}
  const [otp, setOtp] = useState("");

  // Multi-agency picker state — set when /auth/login (or /2fa) returns requires_agency_select.
  const [agencyPick, setAgencyPick] = useState(null); // {challenge_token, agencies:[{id,name,slug}]}

  useEffect(() => {
    if (user) redirectAfterLogin(user, navigate);
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setChallenge({ challenge_token: result.challenge_token, ttl_seconds: result.ttl_seconds });
        setOtp("");
        return;
      }
      if (result?.requires_agency_select) {
        setAgencyPick({ challenge_token: result.challenge_token, agencies: result.agencies });
        return;
      }
      toast.success(`Welcome, ${result.name.split(" ")[0]}`);
      redirectAfterLogin(result, navigate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setSubmitting(true);
    try {
      const u = await submit2fa(challenge.challenge_token, otp.trim());
      if (u?.requires_agency_select) {
        setChallenge(null);
        setAgencyPick({ challenge_token: u.challenge_token, agencies: u.agencies });
        return;
      }
      toast.success(`Welcome, ${u.name.split(" ")[0]}`);
      redirectAfterLogin(u, navigate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  const onPickAgency = async (agencyId) => {
    setSubmitting(true);
    try {
      const u = await selectAgency(agencyPick.challenge_token, agencyId);
      const picked = agencyPick.agencies.find((a) => a.id === agencyId);
      toast.success(`Welcome to ${picked?.name || "your agency"}`);
      redirectAfterLogin(u, navigate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch agency. Please sign in again.");
      setAgencyPick(null);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel2fa = () => {
    setChallenge(null);
    setOtp("");
  };

  const cancelAgencyPick = () => {
    setAgencyPick(null);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-clinical-bg">
      {/* Left — form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <AfyanexLogo markSize={36} showTagline />
          </div>

          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-clinical-text">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-clinical-subtext">
            Documentation, 5× faster. AI does the formatting — you focus on care.
          </p>

          <form onSubmit={challenge ? onSubmit2fa : onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            {agencyPick ? (
              <div data-testid="login-agency-picker">
                <div className="flex items-start gap-2 rounded-lg bg-clinical-bg border border-clinical-border p-3 mb-5">
                  <Building2 className="h-4 w-4 text-clinical-primary mt-0.5 shrink-0" />
                  <div className="text-xs text-clinical-subtext">
                    <p className="font-medium text-clinical-text">Choose an agency</p>
                    <p>This email belongs to multiple agencies. Pick the one you want to sign in to.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {agencyPick.agencies.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      disabled={submitting}
                      onClick={() => onPickAgency(a.id)}
                      className="w-full flex items-center justify-between rounded-xl border border-clinical-border bg-white px-4 py-3.5 text-left hover:border-clinical-primary/40 hover:bg-clinical-primary/[0.02] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid={`agency-pick-${a.slug || a.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0 h-9 w-9 rounded-lg bg-clinical-primary/10 text-clinical-primary flex items-center justify-center">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-clinical-text truncate">{a.name}</div>
                          <div className="text-xs text-clinical-subtext truncate">{a.slug}</div>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-clinical-subtext shrink-0" />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={cancelAgencyPick}
                  className="text-xs text-center w-full text-clinical-subtext hover:text-clinical-text mt-4"
                  data-testid="login-agency-pick-cancel"
                >
                  Use a different account
                </button>
              </div>
            ) : challenge ? (
              <>
                <div className="flex items-start gap-2 rounded-lg bg-clinical-bg border border-clinical-border p-3" data-testid="login-2fa-banner">
                  <KeyRound className="h-4 w-4 text-clinical-primary mt-0.5 shrink-0" />
                  <div className="text-xs text-clinical-subtext">
                    <p className="font-medium text-clinical-text">Two-factor required</p>
                    <p>Open your authenticator app and enter the 6-digit code.</p>
                  </div>
                </div>
                <div>
                  <label className="label-eyebrow mb-1.5 block">Verification code</label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="000000"
                    inputMode="numeric"
                    maxLength={12}
                    className="input-clinical font-mono text-center tracking-[0.4em] text-xl"
                    data-testid="login-2fa-input"
                    autoComplete="one-time-code"
                  />
                  <p className="text-[11px] text-clinical-subtext mt-1.5">
                    Lost your phone? Use one of your backup codes (with the dash).
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={submitting || !otp.trim()}
                  className="btn-primary w-full mt-2"
                  data-testid="login-2fa-submit"
                >
                  {submitting ? "Verifying…" : "Verify & sign in"}
                </button>
                <button
                  type="button"
                  onClick={cancel2fa}
                  className="text-xs text-center w-full text-clinical-subtext hover:text-clinical-text"
                  data-testid="login-2fa-cancel"
                >
                  Use a different account
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="label-eyebrow mb-1.5 block">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@homecare.app"
                    className="input-clinical"
                    data-testid="login-email-input"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="label-eyebrow mb-1.5 block">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-clinical"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full mt-2"
                  data-testid="login-submit-button"
                >
                  {submitting ? "Signing in…" : "Sign in"}
                </button>
                <p className="text-xs text-center mt-2">
                  <a
                    href="/forgot-password"
                    className="text-clinical-subtext hover:text-clinical-primary hover:underline"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </a>
                </p>
                <p className="text-xs text-center text-clinical-subtext mt-2">
                  New agency?{" "}
                  <a
                    href="/agency/signup"
                    className="text-clinical-primary hover:underline font-medium"
                    data-testid="login-signup-link"
                  >
                    Create your account
                  </a>
                </p>
              </>
            )}
          </form>

          {!challenge && !agencyPick && (
            <div className="mt-8 rounded-xl border border-clinical-border bg-white p-5" data-testid="login-trial-cta">
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5 h-9 w-9 rounded-lg bg-clinical-primary/10 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-clinical-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-clinical-text">
                    New to Afyanex?
                  </div>
                  <p className="text-xs text-clinical-subtext mt-0.5">
                    Start a free 14-day trial — no credit card required.
                  </p>
                  <a
                    href="/agency/signup"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-clinical-primary hover:underline"
                    data-testid="login-trial-cta-link"
                  >
                    Create your agency account
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-[11px] text-clinical-subtext">
            <a href="/privacy" className="hover:text-clinical-text hover:underline" data-testid="login-privacy-link">
              Privacy Policy
            </a>
            <span className="mx-2">·</span>
            <a href="/terms" className="hover:text-clinical-text hover:underline" data-testid="login-terms-link">
              Terms of Service
            </a>
          </div>
        </div>
      </div>

      {/* Right — hero panel */}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden text-white"
           style={{ background: "linear-gradient(135deg, #005EFF 0%, #004AE6 55%, #0038B8 100%)" }}>
        {/* Decorative glow blobs */}
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-afyanex-cyan/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-afyanex-pink/30 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]"
             style={{
               backgroundImage:
                 "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)",
               backgroundSize: "22px 22px",
             }}
        />
        <div className="relative z-10 max-w-md px-10">
          <span className="pill bg-white/15 text-white backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" /> AI-assisted notes
          </span>
          <h2 className="mt-6 font-heading text-4xl font-semibold leading-tight">
            Less paperwork.<br />More presence.
          </h2>
          <p className="mt-4 text-white/85 leading-relaxed">
            Caregivers jot quick notes — Afyanex rewrites them into professional clinical
            documentation, ready for audits and the family record.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm border border-white/10">
              <ShieldCheck className="h-4 w-4 mb-1.5" />
              <div className="font-medium">Audit-ready</div>
              <div className="text-white/70 text-xs mt-0.5">Structured notes, PDF exports</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm border border-white/10">
              <Sparkles className="h-4 w-4 mb-1.5" />
              <div className="font-medium">5× faster</div>
              <div className="text-white/70 text-xs mt-0.5">Dictate, format, submit</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
